import React from 'react';
import { FileText, ChevronRight, HardDrive, Sparkles, RefreshCw } from 'lucide-react';

const Sidebar = ({ tabs, activeTab, onTabChange, uploadedFiles, selectedFile, onFileSelect, sidebarOpen, onSidebarOpen, storageUsedBytes, onStartTour, onOpenSettings, demoMode, onTryAnotherDataset }) => {
  const handleTabChange = (tabId) => {
    onTabChange(tabId);
    if (window.innerWidth < 1024) {
      onSidebarOpen(false);
    }
  };

  const handleFileSelect = (file) => {
    onFileSelect(file);
    if (window.innerWidth < 1024) {
      onSidebarOpen(false);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || Number.isNaN(bytes)) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }
    return `${value.toFixed(value < 10 && unitIndex > 0 ? 1 : 0)} ${units[unitIndex]}`;
  };

  return (
    <aside
      className={`
        w-64 bg-sand-100 border-r border-[rgba(32,30,29,0.12)] flex flex-col
        fixed lg:relative inset-y-0 left-0 z-30 lg:z-auto
        transform transition-transform duration-200 ease-in-out lg:transform-none
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}
    >
      {/* Navigation */}
      <div className="p-4">
        <h2 className="text-[11px] font-bold text-sand-600 uppercase tracking-wider mb-3 px-2">Navigation</h2>
        <nav className="space-y-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                data-tour-id={tab.id}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-full transition-colors text-[13px] font-semibold ${isActive
                    ? 'bg-bill-200 text-bill-800'
                    : 'text-sand-700 hover:bg-sand-200 hover:text-ink font-medium'
                  }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-bill-700' : 'text-sand-500'}`} strokeWidth={2.25} />
                <span>{tab.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto opacity-60" />}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Recent Files */}
      <div className="flex-1 px-4 pb-4 overflow-hidden">
        <h3 className="text-[11px] font-bold text-sand-600 uppercase tracking-wider mb-3 px-2">Recent Files</h3>
        <div className="space-y-1 max-h-80 overflow-y-auto custom-scrollbar">
          {uploadedFiles.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-10 h-10 text-sand-400 mx-auto mb-2" />
              <p className="text-sand-600 text-sm">No files uploaded</p>
            </div>
          ) : (
            uploadedFiles.map((file) => (
              <button
                key={file.file_id}
                onClick={() => handleFileSelect(file)}
                className={`w-full text-left p-2.5 rounded-brand-md transition-colors border ${selectedFile?.file_id === file.file_id
                    ? 'bg-white border-[rgba(32,30,29,0.12)] shadow-brand-sm'
                    : 'hover:bg-sand-100 border-transparent'
                  }`}
              >
                <div className="flex items-center gap-2.5">
                  <FileText className={`w-4 h-4 flex-shrink-0 ${selectedFile?.file_id === file.file_id ? 'text-bill-600' : 'text-sand-500'
                    }`} strokeWidth={2.25} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold truncate ${selectedFile?.file_id === file.file_id ? 'text-ink' : 'text-sand-800'
                      }`}>
                      {file.filename}
                    </div>
                    <div className="text-xs text-sand-600 tnum">
                      {file.row_count?.toLocaleString()} rows • {file.columns?.length} cols
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-[rgba(32,30,29,0.12)] space-y-3">
        {/* Demo Mode: Try Another Dataset */}
        {demoMode && onTryAnotherDataset && uploadedFiles.length > 0 && (
          <button
            type="button"
            onClick={() => {
              onTryAnotherDataset();
              if (window.innerWidth < 1024) {
                onSidebarOpen(false);
              }
            }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-full bg-bill-100 border border-bill-300 text-bill-700 hover:bg-bill-200 transition-colors text-sm font-semibold"
          >
            <RefreshCw className="w-4 h-4" />
            Try another dataset
          </button>
        )}

        <div className="rounded-brand-md border border-[rgba(32,30,29,0.12)] bg-white p-3 shadow-brand-sm">
          <div className="flex items-center justify-between text-xs text-sand-600">
            <div className="flex items-center gap-2">
              <HardDrive className="w-4 h-4 text-sand-500" />
              <span>Local Storage</span>
            </div>
            <button
              type="button"
              onClick={() => (onOpenSettings ? onOpenSettings() : handleTabChange('storage'))}
              className="text-sand-700 hover:text-ink transition-colors font-medium"
            >
              Settings
            </button>
          </div>
          <div className="mt-2 text-lg font-bold text-ink tnum">
            {formatBytes(storageUsedBytes)}
          </div>
        </div>
        {onStartTour && (
          <button
            type="button"
            onClick={onStartTour}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-white border border-[rgba(32,30,29,0.12)] text-sand-700 hover:bg-sand-200 transition-colors text-sm font-medium"
          >
            <Sparkles className="w-4 h-4 text-bill-500" />
            Start Tour
          </button>
        )}
        <p className="text-sand-500 text-xs text-center mt-2">
          Powered by DuckDB &amp; AI
        </p>
      </div>
    </aside>
  );
};

export default Sidebar;
